import QRCode from "qrcode";

export const SIZES = { square: [1080, 1080, "Square · 1080×1080"], portrait: [1080, 1920, "Portrait · 1080×1920"], landscape: [1200, 630, "Landscape · 1200×630"] };

export const THEMES = {
  parchment: { name: "Parchment", bg: ["#F6F0E4", "#E9DFC9"], text: "#231D15", accent: "#9A3412", sub: "#6B5D4A" },
  midnight: { name: "Midnight ink", bg: ["#0B0D10", "#161B26"], text: "#F3EBDD", accent: "#F59E0B", sub: "#A8A090" },
  ember: { name: "Ember", bg: ["#2A0E05", "#7C2D12"], text: "#FFF3E0", accent: "#FBBF24", sub: "#F5C9A3" },
  minimal: { name: "Minimal", bg: ["#FFFFFF", "#FFFFFF"], text: "#111111", accent: "#111111", sub: "#6B6B6B" },
};

const SERIF = '"Cormorant Garamond", Georgia, serif';
const SANS = '"Plus Jakarta Sans", sans-serif';

export const loadImage = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });

const wrap = (ctx, text, maxW) => {
  const out = [];
  for (const para of text.split("\n")) {
    let line = "";
    for (const w of para.split(/\s+/)) {
      const t = line ? `${line} ${w}` : w;
      if (ctx.measureText(t).width > maxW && line) { out.push(line); line = w; } else line = t;
    }
    out.push(line);
  }
  return out;
};

const fitQuote = (ctx, paras, maxW, maxH, start) => {
  for (let size = start; size >= 22; size -= 2) {
    ctx.font = `italic 500 ${size}px ${SERIF}`;
    const lines = paras.flatMap((p, i) => [...(i ? [""] : []), ...wrap(ctx, p, maxW)]);
    if (lines.length * size * 1.25 <= maxH) return { size, lines };
  }
  ctx.font = `italic 500 22px ${SERIF}`;
  const lines = paras.flatMap((p) => wrap(ctx, p, maxW));
  return { size: 22, lines: lines.slice(0, Math.floor(maxH / 27.5)) };
};

const drawBg = (ctx, W, H, th, cover) => {
  if (cover) {
    const s = Math.max(W / cover.width, H / cover.height);
    ctx.drawImage(cover, (W - cover.width * s) / 2, (H - cover.height * s) / 2, cover.width * s, cover.height * s);
    ctx.fillStyle = "rgba(8,8,10,0.55)";
    ctx.fillRect(0, 0, W, H);
    return;
  }
  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, th.bg[0]); g.addColorStop(1, th.bg[1]);
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
};

export const quoteText = (q) => q.lines.length > 1 ? q.lines.map((l) => `${l.speaker_name || "Unknown"}: ${l.text}`) : [`“${q.lines[0].text}”`];

export async function drawCard(canvas, { quote, size, theme, cover, show, universeName, story, url, logo }) {
  const [W, H] = SIZES[size];
  const th = cover ? { ...THEMES[theme], text: "#FFF8EC", sub: "#E5DccC" } : THEMES[theme];
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  await Promise.all([document.fonts.load(`italic 500 60px ${SERIF}`), document.fonts.load(`600 24px ${SANS}`)]).catch(() => {});
  drawBg(ctx, W, H, th, cover);
  const u = Math.min(W, H) / 1080;
  const pad = Math.round(W * 0.08);
  ctx.textBaseline = "top";

  let top = pad;
  if (show.universe && universeName) {
    ctx.fillStyle = th.accent; ctx.font = `600 ${Math.round(24 * u)}px ${SANS}`;
    ctx.fillText(universeName.toUpperCase().split("").join(String.fromCharCode(8202)), pad, top);
  }
  if (show.mark && logo) {
    const lh = Math.round(40 * u);
    ctx.font = `600 ${Math.round(24 * u)}px ${SANS}`;
    const tw = ctx.measureText("LoreRight").width;
    ctx.drawImage(logo, W - pad - tw - lh - 10 * u, top - 8 * u, lh, lh);
    ctx.fillStyle = th.sub; ctx.fillText("LoreRight", W - pad - tw, top);
  }
  top += Math.round(70 * u);

  const footH = story ? Math.round((size === "landscape" ? 120 : 170) * u) : 0;
  const speakerH = show.speaker && quote.lines.length === 1 && quote.lines[0].speaker_name ? Math.round(70 * u) : 0;
  const areaH = H - top - pad - footH - speakerH - (story ? 30 * u : 0);
  const { size: fs, lines } = fitQuote(ctx, quoteText(quote), W - pad * 2 - 30 * u, areaH, Math.round(({ landscape: 64, square: 84, portrait: 100 })[size] * u));
  const blockH = lines.length * fs * 1.25;
  let y = top + Math.max(0, (areaH - blockH - speakerH) / 2);
  ctx.fillStyle = th.accent; ctx.fillRect(pad, y, Math.max(4, 5 * u), blockH + speakerH * 0.8);
  ctx.fillStyle = th.text; ctx.font = `italic 500 ${fs}px ${SERIF}`;
  lines.forEach((l) => { ctx.fillText(l, pad + 30 * u, y); y += fs * 1.25; });

  if (speakerH) {
    ctx.fillStyle = th.sub; ctx.font = `500 ${Math.round(32 * u)}px ${SANS}`;
    ctx.fillText(`— ${quote.lines[0].speaker_name}`, pad + 30 * u, y + 24 * u);
  }

  if (story && url) {
    const qs = footH;
    const qx = W - pad - qs, qy = H - pad - qs;
    const qr = document.createElement("canvas");
    await QRCode.toCanvas(qr, url, { margin: 1, width: qs, color: { dark: "#111111", light: "#FFFFFF" } });
    ctx.fillStyle = "#FFFFFF"; ctx.fillRect(qx - 6 * u, qy - 6 * u, qs + 12 * u, qs + 12 * u);
    ctx.drawImage(qr, qx, qy, qs, qs);
    ctx.fillStyle = th.sub; ctx.font = `600 ${Math.round(20 * u)}px ${SANS}`;
    ctx.fillText("READ THE STORY", pad, qy + 4 * u);
    ctx.fillStyle = th.text; ctx.font = `600 ${Math.round(40 * u)}px ${SERIF}`;
    const title = wrap(ctx, story.title, qx - pad - 30 * u)[0];
    ctx.fillText(title, pad, qy + 34 * u);
    const short = url.replace(/^https?:\/\//, ""), maxW = qx - pad - 30 * u;
    let us = Math.round(22 * u);
    ctx.font = `500 ${us}px ${SANS}`;
    while (us > 12 && ctx.measureText(short).width > maxW) { us -= 1; ctx.font = `500 ${us}px ${SANS}`; }
    ctx.fillStyle = th.accent;
    let cut = short.length;
    while (cut > 1 && ctx.measureText(short.slice(0, cut)).width > maxW) cut -= 1;
    ctx.fillText(short.slice(0, cut), pad, qy + 88 * u);
    if (cut < short.length) ctx.fillText(short.slice(cut), pad, qy + 88 * u + us * 1.3);
  }
}
